import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Paper,
  Skeleton,
  Stack,
  Typography,
  Tooltip,
  Box,
  TablePagination,
  Divider,
} from "@mui/material";
import {
  Visibility as ViewIcon,
  FileDownload as DownloadIcon,
  Person as PersonIcon,
  ErrorOutline as ErrorIcon,
  CheckCircleOutline as SuccessIcon,
} from "@mui/icons-material";
import { useQuery } from "react-query";
import { motion, AnimatePresence } from "framer-motion";
import { getBillingHistory } from "../../../api/services/Billing/finance";
import BillingAuditDrawer from "../Drawer/BillingAuditDrawer";

const BillingHistoryTable = () => {
  const [selectedLog, setSelectedLog] = useState(null);
  const [page, setPage] = useState(0);
  const rowsPerPage = 10; // Rows per page fixed logic

  const { data: history, isLoading } = useQuery(
    ["billingHistory", page, rowsPerPage],
    () => getBillingHistory(page + 1, rowsPerPage),
    { keepPreviousData: true }
  );

  const handleChangePage = (event, newPage) => setPage(newPage);

  if (isLoading) {
    return (
      <Box sx={{ mt: 2 }}>
        <Skeleton
          variant="rectangular"
          height={500}
          sx={{ borderRadius: 5, opacity: 0.1 }}
        />
      </Box>
    );
  }

  return (
    <Box
      component={motion.div}
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      sx={{ width: "100%", mt: 3, pb: 6 }}
    >
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          background: "rgba(255, 255, 255, 0.6)",
          backdropFilter: "blur(15px)",
          borderRadius: 5,
          border: "1px solid rgba(255, 255, 255, 0.8)",
          boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.05)",
          overflow: "hidden",
          mb: 4,
        }}
      >
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow
              sx={{
                "& th": {
                  bgcolor: "rgba(241, 245, 249, 0.9)",
                  fontWeight: 800,
                  color: "#475569",
                  fontSize: "0.7rem",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  py: 2.5,
                  borderBottom: "1px solid #e2e8f0",
                },
              }}
            >
              <TableCell>Export ID</TableCell>
              <TableCell>Execution Date</TableCell>
              <TableCell>Billing Period</TableCell>
              <TableCell>Audit Status</TableCell>
              <TableCell>Total Amount</TableCell>
              <TableCell>Authorized By</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <AnimatePresence mode="wait">
              {history?.results?.length > 0 ? (
                history.results.map((row) => (
                  <TableRow
                    key={row.id}
                    component={motion.tr}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    whileHover={{ backgroundColor: "rgba(255, 255, 255, 0.5)" }}
                    sx={{
                      "& td": {
                        py: 1.8,
                        borderBottom: "1px solid rgba(226, 232, 240, 0.5)",
                      },
                    }}
                  >
                    <TableCell>
                      <Typography
                        variant="body2"
                        fontWeight={900}
                        color="primary.main"
                      >
                        #{row.id}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography
                        variant="body2"
                        fontWeight={700}
                        color="#1e293b"
                      >
                        {new Date(row.export_date).toLocaleDateString("en-GB", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        })}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        fontWeight={600}
                      >
                        {new Date(row.export_date).toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Stack direction="row" spacing={0.8} alignItems="center">
                        <Chip
                          label={row.start_range}
                          size="small"
                          sx={{
                            height: 20,
                            borderRadius: 1.5,
                            fontSize: "0.65rem",
                            fontWeight: 700,
                            bgcolor: "#f0f9ff",
                            color: "#0369a1",
                            border: "1px solid #bae6fd",
                          }}
                        />
                        <Typography
                          variant="caption"
                          sx={{ color: "#94a3b8", fontSize: "10px" }}
                        >
                          TO
                        </Typography>
                        <Chip
                          label={row.end_range}
                          size="small"
                          sx={{
                            height: 20,
                            borderRadius: 1.5,
                            fontSize: "0.65rem",
                            fontWeight: 700,
                            bgcolor: "#f0f9ff",
                            color: "#0369a1",
                            border: "1px solid #bae6fd",
                          }}
                        />
                      </Stack>
                    </TableCell>

                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        <Chip
                          icon={<SuccessIcon style={{ fontSize: 14 }} />}
                          label={row.total_jobs}
                          size="small"
                          sx={{
                            height: 22,
                            fontSize: "0.7rem",
                            fontWeight: 800,
                            bgcolor: "#ecfdf5",
                            color: "#065f46",
                          }}
                        />
                        {row.failed_jobs > 0 && (
                          <Chip
                            icon={<ErrorIcon style={{ fontSize: 14 }} />}
                            label={row.failed_jobs}
                            size="small"
                            sx={{
                              height: 22,
                              fontSize: "0.7rem",
                              fontWeight: 800,
                              bgcolor: "#fef2f2",
                              color: "#991b1b",
                            }}
                          />
                        )}
                      </Stack>
                    </TableCell>

                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 900, color: "#0f172a" }}
                      >
                        £
                        {parseFloat(row.total_amount || 0).toLocaleString(
                          undefined,
                          { minimumFractionDigits: 2 }
                        )}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Box
                          sx={{
                            width: 22,
                            height: 22,
                            bgcolor: "#f1f5f9",
                            borderRadius: "50%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <PersonIcon sx={{ fontSize: 12, color: "#64748b" }} />
                        </Box>
                        <Typography
                          variant="caption"
                          fontWeight={700}
                          color="#475569"
                        >
                          {row.exported_by}
                        </Typography>
                      </Stack>
                    </TableCell>

                    <TableCell align="right">
                      <Stack
                        direction="row"
                        spacing={1}
                        justifyContent="flex-end"
                      >
                        <IconButton
                          onClick={() => setSelectedLog(row)}
                          size="small"
                          sx={{
                            color: "primary.main",
                            bgcolor: "#fff",
                            border: "1px solid #e2e8f0",
                          }}
                        >
                          <ViewIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                        <IconButton
                          component="a"
                          href={row.export_file}
                          download
                          size="small"
                          sx={{
                            color: "success.main",
                            bgcolor: "#fff",
                            border: "1px solid #e2e8f0",
                          }}
                        >
                          <DownloadIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 10 }}>
                    No records found.
                  </TableCell>
                </TableRow>
              )}
            </AnimatePresence>
          </TableBody>
        </Table>
      </TableContainer>

      {/* --- Ultra-Clean Centered Pagination --- */}
      <Stack direction="row" justifyContent="center" sx={{ mt: 2 }}>
        <Box
          component={motion.div}
          whileHover={{ y: -2 }}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            px: 1.5,
            py: 0.5,
            background: "rgba(30, 41, 59, 0.04)", // Subtle tinted background
            borderRadius: 8,
            border: "1px solid rgba(226, 232, 240, 0.6)",
          }}
        >
          {/* Page Counter Display */}
          <Box
            sx={{
              px: 2,
              py: 0.8,
              bgcolor: "#fff",
              borderRadius: 6,
              boxShadow: "0 2px 4px rgba(0,0,0,0.02)",
              display: "flex",
              alignItems: "center",
              border: "1px solid #f1f5f9",
            }}
          >
            <Typography
              sx={{
                fontSize: "0.7rem",
                fontWeight: 900,
                color: "#64748b",
                letterSpacing: "0.02em",
              }}
            >
              PAGE
            </Typography>
            <Typography
              sx={{
                fontSize: "0.85rem",
                fontWeight: 900,
                color: "#6366f1",
                mx: 1.5,
              }}
            >
              {page + 1}{" "}
              <span style={{ color: "#cbd5e1", fontWeight: 500 }}>/</span>{" "}
              {Math.ceil((history?.count || 0) / rowsPerPage)}
            </Typography>
          </Box>

          <TablePagination
            component="div"
            count={history?.count || 0}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[]}
            labelRowsPerPage=""
            sx={{
              border: "none",
              ml: 1,
              ".MuiTablePagination-toolbar": { minHeight: 45, p: 0 },
              ".MuiTablePagination-spacer": { display: "none" },
              ".MuiTablePagination-displayedRows": { display: "none" }, // Hiding redundant text
              ".MuiTablePagination-actions": {
                m: 0,
                "& button": {
                  bgcolor: "#fff",
                  color: "#1e293b",
                  border: "1px solid #e2e8f0",
                  borderRadius: 5,
                  mx: 0.5,
                  width: 36,
                  height: 36,
                  "&:hover": {
                    bgcolor: "#6366f1",
                    color: "#fff",
                    borderColor: "#6366f1",
                    boxShadow: "0 4px 12px rgba(99, 102, 241, 0.2)",
                  },
                  "&.Mui-disabled": { opacity: 0.2 },
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                },
              },
            }}
          />

          <Divider
            orientation="vertical"
            flexItem
            sx={{ mx: 2, my: 1, borderColor: "rgba(0,0,0,0.06)" }}
          />

          <Typography
            variant="caption"
            sx={{
              fontWeight: 800,
              color: "#94a3b8",
              pr: 2,
              fontSize: "0.65rem",
            }}
          >
            TOTAL {history?.count || 0} ITEMS
          </Typography>
        </Box>
      </Stack>

      <BillingAuditDrawer
        logData={selectedLog}
        onClose={() => setSelectedLog(null)}
      />
    </Box>
  );
};

export default BillingHistoryTable;